import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { 
  Users,
  UserPlus,
  ArrowLeft,
  Mail,
  Trash2,
  Edit2,
  Shield,
  Crown,
  User,
  Check,
  X
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorDashboard.css';

const OrganizationTeamManagement = () => {
  const { orgSlug } = useParams();
  const context = useOutletContext();
  
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('editor');
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  // Helper to add Cloudinary transformation
  const addCloudinaryTransform = (url, transform = 't_square-150/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };

  const canManageTeam = currentUserRole === 'owner' || currentUserRole === 'admin';

  useEffect(() => {
    if (orgSlug) {
      loadOrganizationData();
    }
  }, [orgSlug]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);

      // Fetch organization by slug
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('slug', orgSlug)
        .single();

      if (orgError) {
        console.error('Error fetching organization:', orgError);
        setLoading(false);
        return;
      }

      setOrganization(org);

      // Fetch members
      const { data: membersData, error: membersError } = await supabase
        .from('organization_members')
        .select(`
          *,
          users:user_uuid (
            uuid,
            first_name,
            last_name,
            email,
            profile_img
          )
        `)
        .eq('organization_id', org.organization_id)
        .eq('is_active', true)
        .order('role', { ascending: true });

      if (membersError) {
        console.error('Error fetching members:', membersError);
      } else {
        setMembers(membersData || []);
      }

      // Check current user's role
      const { data: { user } } = await supabase.auth.getUser();
      if (user && membersData) {
        const currentMember = membersData.find(m => m.user_uuid === user.id);
        if (currentMember) {
          setCurrentUserRole(currentMember.role);
        }
      }

    } catch (error) {
      console.error('Error loading organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      setAddError('Please enter an email address');
      return;
    }

    setAddError('');
    setAddSuccess('');

    try {
      // Find user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('uuid, email, first_name, last_name')
        .eq('email', newMemberEmail.trim().toLowerCase())
        .single();

      if (userError || !userData) {
        setAddError('No user found with that email address. They must have a VibrantCast account first.');
        return;
      }

      // Check if already a member
      const existingMember = members.find(m => m.user_uuid === userData.uuid);
      if (existingMember) {
        setAddError('This user is already a team member.');
        return;
      }

      // Add member
      const { error: insertError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: organization.organization_id,
          user_uuid: userData.uuid,
          role: newMemberRole,
          is_active: true
        });

      if (insertError) {
        console.error('Error adding member:', insertError);
        setAddError('Failed to add team member. Please try again.');
        return;
      }

      setAddSuccess(`${userData.first_name || userData.email} has been added as ${newMemberRole}.`);
      setNewMemberEmail('');
      setNewMemberRole('editor');
      
      // Reload members
      await loadOrganizationData();
      
      // Close modal after a delay
      setTimeout(() => {
        setShowAddModal(false);
        setAddSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Error adding member:', error);
      setAddError('An error occurred. Please try again.');
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) {
        console.error('Error updating role:', error);
        return;
      }

      setEditingMember(null);
      await loadOrganizationData();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemoveMember = async (member) => {
    if (member.role === 'owner') {
      alert('Cannot remove the owner. Transfer ownership first.');
      return;
    }

    if (!confirm(`Are you sure you want to remove ${member.users?.first_name || member.users?.email} from the team?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ is_active: false })
        .eq('id', member.id);

      if (error) {
        console.error('Error removing member:', error);
        return;
      }

      await loadOrganizationData();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown size={16} color="#f59e0b" />;
      case 'admin': return <Shield size={16} color="#3b82f6" />;
      default: return <User size={16} color="#6c757d" />;
    }
  };

  if (loading) {
    return (
      <div className="curator-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading team...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="curator-overview">
        <div className="empty-state">
          <h2>Organization Not Found</h2>
          <p>The organization could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-overview">
      {/* Header */}
      <div className="team-management-header">
        <Link to={`/curator/organization/${orgSlug}`} className="back-link">
          <ArrowLeft size={16} />
          Back to {organization.name}
        </Link>
        
        <div className="team-header-content">
          <h1>Team Members</h1>
          <p>{members.length} member{members.length !== 1 ? 's' : ''} in {organization.name}</p>
        </div>

        {canManageTeam && (
          <button 
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <UserPlus size={18} />
            Add Member
          </button>
        )}
      </div>

      {/* Members List */}
      <div className="team-members-grid">
        {members.map(member => (
          <div key={member.id} className="team-member-card">
            <div className="member-avatar-large">
              {member.users?.profile_img ? (
                <img src={addCloudinaryTransform(member.users.profile_img)} alt="" />
              ) : (
                <div className="member-avatar-placeholder-large">
                  {member.users?.first_name?.[0] || member.users?.email?.[0] || '?'}
                </div>
              )}
            </div>
            
            <div className="member-details">
              <h3>
                {member.users?.first_name && member.users?.last_name 
                  ? `${member.users.first_name} ${member.users.last_name}`
                  : member.users?.email || 'Unknown'}
              </h3>
              <p className="member-email">{member.users?.email}</p>
              
              <div className="member-role-badge">
                {getRoleIcon(member.role)}
                {editingMember === member.id ? (
                  <select 
                    value={member.role}
                    onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                    className="role-select"
                    autoFocus
                    onBlur={() => setEditingMember(null)}
                  >
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                    {currentUserRole === 'owner' && <option value="owner">Owner</option>}
                  </select>
                ) : (
                  <span className={`role-label role-${member.role}`}>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </span>
                )}
              </div>
            </div>

            {canManageTeam && member.role !== 'owner' && (
              <div className="member-actions">
                <button 
                  className="action-btn edit"
                  onClick={() => setEditingMember(member.id)}
                  title="Change Role"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleRemoveMember(member)}
                  title="Remove Member"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Team Member</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  className="form-select"
                >
                  <option value="editor">Editor - Can edit collections and content</option>
                  <option value="admin">Admin - Can manage team and settings</option>
                </select>
              </div>

              {addError && (
                <div className="alert alert-error">{addError}</div>
              )}
              
              {addSuccess && (
                <div className="alert alert-success">
                  <Check size={16} />
                  {addSuccess}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleAddMember}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationTeamManagement;
